// Proxies.js - Updated with responsive mobile design and cursor
import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Modal, InputGroup, Alert, Spinner, Toast, ToastContainer, ListGroup, Tabs, Tab } from 'react-bootstrap';
import { Plus, Edit, Trash2, Server, Activity, Search, AlertTriangle, CheckCircle, Users, Key, RefreshCw, Upload, Download, FileText, File, ChevronDown, ChevronUp } from 'lucide-react';
import { getProxies, createProxy, updateProxy, deleteProxy, autoAssignTokens,validateProxyTokens } from '../services/proxies';
import { getTokens, bindTokenToProxy, unbindTokenFromProxy } from '../services/tokens';
import { uploadProxiesCSV, uploadProxiesJSON, generateCSVTemplate, generateJSONTemplate } from '../services/bulkUpload';
import { getProxyBindings } from '../services/bindings'

const Proxies = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProxy, setEditingProxy] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [proxies, setProxies] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [formData, setFormData] = useState({
    proxy: '',
    is_active: true,
    usage_count: 0,
    fail_count: 0,
    success_count: 0
  });
  const [submitting, setSubmitting] = useState(false);
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [proxyToDelete, setProxyToDelete] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [selectedProxy, setSelectedProxy] = useState(null);
  const [availableTokens, setAvailableTokens] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('csv');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const fileInputRef = useRef(null);
  const [proxyValidations, setProxyValidations] = useState({});
  const [bindings, setBindings] = useState([]);

  // Fetch proxies and tokens from API
  const fetchProxies = async () => {
    try {
      setLoading(true);
      const response = await getProxies();
      setProxies(response);
    } catch (error) {
      console.error('Failed to fetch proxies:', error);
      showToast('Failed to load proxies: ' + (error.message || error), 'danger');
    } finally {
      setLoading(false);
    }
  };

  const fetchTokens = async () => {
    try {
      setTokensLoading(true);
      const response = await getTokens();
      setTokens(response);
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
    } finally {
      setTokensLoading(false);
    }
  };

  useEffect(() => {
    fetchProxies();
    fetchTokens();
  }, []);
  useEffect(() => {
  const fetchAllBindings = async () => {
    try {
      // You might want to create an endpoint that returns all bindings
      // or fetch them per proxy as needed
      const allBindings = [];
      for (const proxy of proxies) {
        const proxyBindings = await getProxyBindings(proxy.id);
        allBindings.push(...proxyBindings);
      }
      setBindings(allBindings);
    } catch (error) {
      console.error('Failed to fetch bindings:', error);
    }
  };
  
  if (proxies.length > 0) {
    fetchAllBindings();
  }
}, [proxies]);

const getProxyTokens = (proxyId) => {
  const boundTokenIds = bindings
    .filter(binding => binding.proxy_id === proxyId && binding.is_active)
    .map(binding => binding.token_id);
  
  return tokens.filter(token => boundTokenIds.includes(token.id));
};

  // Show toast notification
  const showToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setToastShow(true);
  };

  const getStatusVariant = (isActive) => {
    return isActive ? 'success' : 'secondary';
  };

  const getProxyTypeFromString = (proxyString) => {
    if (!proxyString) return 'Unknown';
    if (proxyString.includes('socks5')) return 'SOCKS5';
    if (proxyString.includes('https')) return 'HTTPS';
    return 'HTTP';
  };

  const parseProxyString = (proxyString) => {
    if (!proxyString) return { host: '', port: '' };
    
    // Handle different proxy formats
    if (proxyString.includes('://')) {
      const [protocol, rest] = proxyString.split('://');
      const [auth, hostPort] = rest.includes('@') ? rest.split('@') : ['', rest];
      const [host, port] = hostPort.includes(':') ? hostPort.split(':') : [hostPort, ''];
      return { host, port: port || '8080' };
    }
    
    // Handle ip:port format
    if (proxyString.includes(':')) {
      const [host, port] = proxyString.split(':');
      return { host, port };
    }
    
    return { host: proxyString, port: '8080' };
  };

  const getClientTypeBadge = (clientType) => {
    switch (clientType) {
      case 'web': return 'primary';
      case 'desktop': return 'info';
      case 'mobile': return 'success';
      default: return 'secondary';
    }
  };


useEffect(() => {
  const fetchValidations = async () => {
    const validations = {};
    for (const proxy of proxies) {
      try {
        const response = await validateProxyTokens(proxy.id);
        validations[proxy.id] = response.validation;
      } catch (error) {
        console.error(`Failed to fetch validation for proxy ${proxy.id}:`, error);
        validations[proxy.id] = {
          hasWeb: false,
          hasDesktop: false,
          hasMobile: false,
          isValid: false,
          tokenCount: 0,
          clientTypes: []
        };
      }
    }
    setProxyValidations(validations);
  };

  if (proxies.length > 0) {
    fetchValidations();
  }
}, [proxies]);

const filteredProxies = proxies.filter(proxy => {
  const matchesSearch = proxy.proxy.toLowerCase().includes(searchTerm.toLowerCase());
  const validation = proxyValidations[proxy.id] || {
    has_web: false,
    has_desktop: false,
    has_mobile: false,
    isValid: false,
    tokenCount: 0
  };
  
  // CORRECTION: Forcer la validation si les trois types sont présents
  const isValid = validation.isValid || (validation.has_web && validation.has_desktop && validation.has_mobile);
  
  const matchesFilter = filterStatus === 'all' || 
                      (filterStatus === 'active' && proxy.is_active) ||
                      (filterStatus === 'inactive' && !proxy.is_active) ||
                      (filterStatus === 'valid' && isValid) ||
                      (filterStatus === 'invalid' && !isValid);
  
  return matchesSearch && matchesFilter;
});

  const toggleRowExpansion = (proxyId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(proxyId)) {
      newExpandedRows.delete(proxyId);
    } else {
      newExpandedRows.add(proxyId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleEdit = (proxy) => {
    setEditingProxy(proxy);
    setFormData({
      proxy: proxy.proxy,
      is_active: proxy.is_active,
      usage_count: proxy.usage_count || 0,
      fail_count: proxy.fail_count || 0,
      success_count: proxy.success_count || 0
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingProxy(null);
    setFormData({
      proxy: '',
      is_active: true,
      usage_count: 0,
      fail_count: 0,
      success_count: 0
    });
    setShowModal(true);
  };

  const handleDeleteClick = (proxy) => {
    setProxyToDelete(proxy);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (proxyToDelete) {
      try {
        await deleteProxy(proxyToDelete.id);
        fetchProxies();
        showToast('Proxy deleted successfully');
      } catch (error) {
        console.error('Failed to delete proxy:', error);
        showToast('Failed to delete proxy: ' + (error.message || error), 'danger');
      } finally {
        setShowDeleteModal(false);
        setProxyToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProxyToDelete(null);
  };

const handleManageTokens = async (proxy) => {
  setSelectedProxy(proxy);
  
  try {
    // Get tokens currently bound to this proxy
    const boundTokens = getProxyTokens(proxy.id);
    
    // Find available tokens (not bound to this proxy)
    const available = tokens.filter(token => 
      !boundTokens.some(boundToken => boundToken.id === token.id)
    );
    
    setAvailableTokens(available);
    setShowTokenModal(true);
  } catch (error) {
    console.error('Failed to fetch proxy tokens:', error);
    showToast('Failed to load tokens: ' + error.message, 'danger');
  }
};

  const handleBindToken = async (tokenId) => {
    try {
      await bindTokenToProxy(tokenId, selectedProxy.id);
      showToast('Token bound to proxy successfully');
      fetchTokens();
      fetchProxies();
    } catch (error) {
      showToast('Failed to bind token: ' + error.message, 'danger');
    }
  };

  const handleUnbindToken = async (tokenId, proxyId) => {
  try {
    await unbindTokenFromProxy(tokenId, proxyId);
    showToast('Token unbound from proxy successfully');
    fetchTokens();
    fetchProxies();
  } catch (error) {
    showToast('Failed to unbind token: ' + error.message, 'danger');
  }
};



const handleAutoAssign = async (proxyId) => {
  try {
    const result = await autoAssignTokens(proxyId);
    
    let message = 'Token assignment completed';
    let variant = 'success';
    
    if (result.validation_result) {
      const vr = result.validation_result;
      const assignedCount = vr.assigned_tokens?.length || 0;
      
      // CORRECTION: Utiliser la validation du serveur comme source de vérité
      const isValid = vr.validation_after?.isValid || false;
      const hasWeb = vr.validation_after?.has_web || false;
      const hasDesktop = vr.validation_after?.has_desktop || false;
      const hasMobile = vr.validation_after?.has_mobile || false;
      
      if (isValid) {
        message = 'All 3 tokens assigned successfully!';
      } else if (assignedCount > 0) {
        const missing = [];
        if (!hasWeb) missing.push('web');
        if (!hasDesktop) missing.push('desktop');
        if (!hasMobile) missing.push('mobile');
        
        message = `Assigned ${assignedCount} tokens, still missing: ${missing.join(', ')}`;
        variant = 'warning';
      } else {
        message = 'No tokens were assigned';
        variant = 'warning';
      }
    }
    
    showToast(message, variant);
    
    // Refresh both tokens and validations
    await fetchTokens();
    await fetchProxies();
    
  } catch (error) {
    showToast('Failed to auto-assign tokens: ' + error.message, 'danger');
  }
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingProxy) {
        await updateProxy(editingProxy.id, formData);
        showToast('Proxy updated successfully');
      } else {
        await createProxy(formData);
        showToast('Proxy created successfully');
      }
      setShowModal(false);
      fetchProxies();
    } catch (error) {
      console.error('Failed to save proxy:', error);
      showToast('Failed to save proxy: ' + (error.message || error), 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Bulk upload functions
  const handleShowUploadModal = () => {
    setShowUploadModal(true);
    setUploadResult(null);
    setUploadFile(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
    }
  };
  const handleUnbindTokenByType = async (proxyId, clientType) => {
  try {
    // Find the token with this client type bound to this proxy
    const tokenToUnbind = tokens.find(token => 
      token.client_type === clientType && 
      bindings.some(binding => 
        binding.token_id === token.id && 
        binding.proxy_id === proxyId
      )
    );
    
    if (tokenToUnbind) {
      await unbindTokenFromProxy(tokenToUnbind.id);
      showToast(`${clientType} token unbound successfully`);
      fetchTokens();
      fetchProxies();
    }
  } catch (error) {
    showToast('Failed to unbind token: ' + error.message, 'danger');
  }
};

  const handleUpload = async () => {
    if (!uploadFile) {
      showToast('Please select a file first', 'danger');
      return;
    }

    setUploading(true);
    try {
      let result;
      if (uploadType === 'csv') {
        result = await uploadProxiesCSV(uploadFile);
      } else {
        result = await uploadProxiesJSON(uploadFile);
      }
      
      setUploadResult(result);
      if (result.errors && result.errors.length > 0) {
        showToast(`Upload completed with ${result.errors.length} errors`, 'warning');
      } else {
        showToast(`Successfully uploaded ${result.proxies_created || result.message} proxies`, 'success');
        setShowUploadModal(false);
        fetchProxies(); // Refresh the proxies list
      }
    } catch (error) {
      console.error('Upload failed:', error);
      showToast('Upload failed: ' + (error.message || error), 'danger');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = uploadType === 'csv' 
      ? generateCSVTemplate('proxies') 
      : generateJSONTemplate('proxies');
    
    const blob = new Blob([template], { type: uploadType === 'csv' ? 'text/csv' : 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `proxies-template.${uploadType}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const maskToken = (token) => {
    if (!token) return 'N/A';
    if (token.length <= 8) return token;
    return token.substring(0, 4) + '...' + token.substring(token.length - 4);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading proxies...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast 
          show={toastShow} 
          onClose={() => setToastShow(false)} 
          delay={5000} 
          autohide
          bg={toastVariant}
        >
          <Toast.Header className={`bg-${toastVariant} text-white justify-content-between`}>
            <div className="d-flex align-items-center">
              <CheckCircle size={18} className="me-2" />
              <strong className="me-auto">
                {toastVariant === 'success' ? 'Success' : 'Error'}
              </strong>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={() => setToastShow(false)}
              aria-label="Close"
            ></button>
          </Toast.Header>
          <Toast.Body className="bg-light">
            <div className="d-flex align-items-center">
              <div className="toast-message flex-grow-1">
                {toastMessage}
              </div>
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h1 className="h2 fw-bold mb-1">Proxy Management</h1>
          <p className="text-muted mb-0">
            Configure and monitor your proxy servers with 3 tokens each (web, desktop, mobile)
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={handleShowUploadModal}>
            <Upload size={16} className="me-2" />
            <span className="d-none d-md-inline">Bulk Upload</span>
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            <Plus size={16} className="me-2" />
            <span className="d-none d-md-inline">Add Proxy</span>
            <span className="d-md-none">Add</span>
          </Button>
        </div>
      </div>

      {/* Info Alert */}
      <Alert variant="info" className="mb-4">
        <AlertTriangle size={20} className="me-2" />
        Each proxy should have exactly 3 tokens: one web, one desktop, and one mobile client type for optimal performance.
      </Alert>

      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Server size={24} className="text-primary mb-2" />
              <h5 className="fw-bold mb-0">{proxies.length}</h5>
              <p className="text-muted mb-0 small">Total Proxies</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Activity size={24} className="text-success mb-2" />
              <h5 className="fw-bold mb-0">{proxies.filter(p => p.is_active).length}</h5>
              <p className="text-muted mb-0 small">Active</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body className="text-center p-3">
                <Users size={24} className="text-info mb-2" />
                <h5 className="fw-bold mb-0">
                  {proxies.filter(p => {
                    const validation = proxyValidations[p.id] || {};
                    // CORRECTION: Utiliser les bons noms de champs
                    return validation.isValid || 
                          (validation.has_web && validation.has_desktop && validation.has_mobile);
                  }).length}
                </h5>
                <p className="text-muted mb-0 small">Valid Config</p>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} sm={6} lg={3}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body className="text-center p-3">
                <div className="text-warning mb-2">⚠️</div>
                <h5 className="fw-bold mb-0">
                  {proxies.filter(p => {
                    const validation = proxyValidations[p.id] || {};
                    // CORRECTION: Utiliser les bons noms de champs
                    return !validation.isValid && 
                          !(validation.has_web && validation.has_desktop && validation.has_mobile);
                  }).length}
                </h5>
                <p className="text-muted mb-0 small">Needs Setup</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

      {/* Filters */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-3">
          <Row className="g-2">
            <Col xs={12} md={6}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search proxies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={4}>
              <Form.Select
                size="sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="valid">Valid Config</option>
                <option value="invalid">Invalid Config</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={2}>
              <div className="text-muted small d-flex align-items-center h-100 justify-content-end justify-content-md-start">
                {filteredProxies.length} proxies
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Proxies Table */}
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {/* Desktop Table */}
          <div className="d-none d-md-block table-responsive">
            <Table className="mb-0" hover>
              <thead className="bg-light">
                <tr>
                  <th className="border-0 py-3 px-4">Proxy</th>
                  <th className="border-0 py-3">Type</th>
                  <th className="border-0 py-3">Status</th>
                  <th className="border-0 py-3">Token Config</th>
                  <th className="border-0 py-3">Usage Stats</th>
                  <th className="border-0 py-3">Last Used</th>
                  <th className="border-0 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
              {filteredProxies.map((proxy) => {
                const { host, port } = parseProxyString(proxy.proxy);
                const proxyType = getProxyTypeFromString(proxy.proxy);
                const validation = proxyValidations[proxy.id] || {
                  hasWeb: false,
                  hasDesktop: false,
                  hasMobile: false,
                  isValid: false,
                  tokenCount: 0,
                  clientTypes: []
                };
                
                // Use clientTypes array as fallback for broken has_* fields
                const hasWeb = validation.has_web || validation.clientTypes?.includes('web') || false;
                const hasDesktop = validation.has_desktop || validation.clientTypes?.includes('desktop') || false;
                const hasMobile = validation.has_mobile || validation.clientTypes?.includes('mobile') || false;
                const isValid = validation.isValid || (validation.has_web && validation.has_desktop && validation.has_mobile);
                
                const proxyTokens = getProxyTokens(proxy.id);
                  
                  return (
                    <tr key={proxy.id} style={{ cursor: 'pointer' }}>
                      <td className="px-4 py-3" onClick={() => toggleRowExpansion(proxy.id)}>
                        <div className="fw-medium font-monospace small">{host}</div>
                        {port && <small className="text-muted">Port: {port}</small>}
                      </td>
                      <td className="py-3" onClick={() => toggleRowExpansion(proxy.id)}>
                        <Badge bg="info" className="text-capitalize">
                          {proxyType}
                        </Badge>
                      </td>
                      <td className="py-3" onClick={() => toggleRowExpansion(proxy.id)}>
                        <Badge bg={getStatusVariant(proxy.is_active)} className="text-capitalize">
                          {proxy.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3" onClick={() => toggleRowExpansion(proxy.id)}>
                        <div className="d-flex flex-column gap-1">
                          <div className="d-flex align-items-center gap-1">
                            <Badge bg={validation.has_web ? 'success' : 'secondary'} className="text-capitalize">
                              Web {validation.has_web ? '✓' : '✗'}
                            </Badge>
                            <Badge bg={validation.has_desktop ? 'success' : 'secondary'} className="text-capitalize">
                              Desktop {validation.has_desktop ? '✓' : '✗'}
                            </Badge>
                            <Badge bg={validation.has_mobile ? 'success' : 'secondary'} className="text-capitalize">
                              Mobile {validation.has_mobile ? '✓' : '✗'}
                            </Badge>
                          </div>
                          <small className={isValid ? 'text-success' : 'text-warning'}>
                            {validation.tokenCount}/3 tokens • {isValid ? 'Valid' : 'Invalid'}
                          </small>
                        </div>
                      </td>
                      <td className="py-3" onClick={() => toggleRowExpansion(proxy.id)}>
                        <div className="d-flex flex-column">
                          <small>Success: {proxy.success_count || 0}</small>
                          <small>Fails: {proxy.fail_count || 0}</small>
                          <small>Total: {proxy.usage_count || 0}</small>
                        </div>
                      </td>
                      <td className="py-3" onClick={() => toggleRowExpansion(proxy.id)}>
                        <small className="text-muted">{formatDate(proxy.last_used)}</small>
                      </td>
                      <td className="py-3">
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleManageTokens(proxy)}
                            title="Manage Tokens"
                          >
                            <Key size={14} />
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => handleAutoAssign(proxy.id)}
                            title="Auto Assign Tokens"
                            disabled={validation.isValid}
                          >
                            <RefreshCw size={14} />
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(proxy)}
                            title="Edit Proxy"
                          >
                            <Edit size={14} />
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDeleteClick(proxy)}
                            title="Delete Proxy"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="d-md-none">
            {filteredProxies.map((proxy) => {
              const { host, port } = parseProxyString(proxy.proxy);
              const proxyType = getProxyTypeFromString(proxy.proxy);
              const validation = proxyValidations[proxy.id] || {
                has_web: false,
                has_desktop: false,
                has_mobile: false,
                isValid: false,
                tokenCount: 0,
                clientTypes: []
              };
              
              // CORRECTION: Utiliser les bons noms de champs
              const hasWeb = validation.has_web || validation.clientTypes?.includes('web') || false;
              const hasDesktop = validation.has_desktop || validation.clientTypes?.includes('desktop') || false;
              const hasMobile = validation.has_mobile || validation.clientTypes?.includes('mobile') || false;
              const isValid = validation.isValid || (hasWeb && hasDesktop && hasMobile);
              
              const isExpanded = expandedRows.has(proxy.id);
              
              return (
                <Card key={proxy.id} className="mb-3 border-0 shadow-sm">
                  <Card.Body 
                    className="p-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleRowExpansion(proxy.id)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1">
                          <div className="fw-medium font-monospace small me-2">{host}</div>
                          <Badge bg="info" className="text-capitalize me-1">
                            {proxyType}
                          </Badge>
                          <Badge bg={getStatusVariant(proxy.is_active)} className="text-capitalize">
                            {proxy.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        {port && <small className="text-muted d-block">Port: {port}</small>}
                        <div className="mt-2">
                          <small className={validation.isValid ? 'text-success' : 'text-warning'}>
                            {validation.tokenCount}/3 tokens • {validation.isValid ? 'Valid' : 'Invalid'}
                          </small>
                        </div>
                      </div>
                      <div>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-top">
                        <Row className="g-3">
                          <Col xs={6}>
                            <div className="text-muted small">Token Config</div>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {/* CORRECTION: Utiliser les variables corrigées */}
                              <Badge bg={hasWeb ? 'success' : 'secondary'} className="text-capitalize">
                                Web {hasWeb ? '✓' : '✗'}
                              </Badge>
                              <Badge bg={hasDesktop ? 'success' : 'secondary'} className="text-capitalize">
                                Desktop {hasDesktop ? '✓' : '✗'}
                              </Badge>
                              <Badge bg={hasMobile ? 'success' : 'secondary'} className="text-capitalize">
                                Mobile {hasMobile ? '✓' : '✗'}
                              </Badge>
                            </div>
                          </Col>
                          <Col xs={6}>
                            <div className="text-muted small">Usage Stats</div>
                            <div className="small">
                              <div>Success: {proxy.success_count || 0}</div>
                              <div>Fails: {proxy.fail_count || 0}</div>
                              <div>Total: {proxy.usage_count || 0}</div>
                            </div>
                          </Col>
                          <Col xs={12}>
                            <div className="text-muted small">Last Used</div>
                            <div className="small">{formatDate(proxy.last_used)}</div>
                          </Col>
                          <Col xs={12}>
                            <div className="d-flex gap-2 justify-content-center mt-2">
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleManageTokens(proxy);
                                }}
                                title="Manage Tokens"
                              >
                                <Key size={14} />
                              </Button>
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAutoAssign(proxy.id);
                                }}
                                title="Auto Assign Tokens"
                                disabled={validation.isValid}
                              >
                                <RefreshCw size={14} />
                              </Button>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(proxy);
                                }}
                                title="Edit Proxy"
                              >
                                <Edit size={14} />
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(proxy);
                                }}
                                title="Delete Proxy"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
          </div>

          {filteredProxies.length === 0 && (
            <div className="text-center py-5 text-muted">
              No proxies found. {searchTerm ? 'Try a different search term.' : 'Add your first proxy!'}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            {editingProxy ? (
              <>
                <Edit size={20} className="me-2 text-primary" />
                Edit Proxy
              </>
            ) : (
              <>
                <Plus size={20} className="me-2 text-primary" />
                Add New Proxy
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="pt-0">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Proxy Address</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="host:port or protocol://user:pass@host:port"
                    value={formData.proxy}
                    onChange={(e) => handleInputChange('proxy', e.target.value)}
                    className="py-2"
                    required
                  />
                  <Form.Text className="text-muted">
                    Enter proxy in format: host:port or protocol://user:pass@host:port
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Status</Form.Label>
                  <Form.Select 
                    value={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.value === 'true')}
                    className="py-2"
                  >
                    <option value={true}>✅ Active</option>
                    <option value={false}>❌ Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0">
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowModal(false)} 
              disabled={submitting}
              className="px-4"
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={submitting}
              className="px-4"
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingProxy ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editingProxy ? 'Update Proxy' : 'Add Proxy'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Token Management Modal */}
      <Modal show={showTokenModal} onHide={() => setShowTokenModal(false)} size="xl" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            <Key size={20} className="me-2 text-primary" />
            Manage Tokens for {selectedProxy?.proxy}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
        {selectedProxy && (
          <Row>
            <Col md={6}>
              <h5 className="fw-bold mb-3">Current Tokens</h5>
              {tokensLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
            <ListGroup variant="flush">
              {getProxyTokens(selectedProxy.id).map(token => (
                <ListGroup.Item key={token.id} className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-medium">{token.label || 'Unlabeled'}</div>
                    <small className="text-muted font-monospace">{maskToken(token.token)}</small>
                    <Badge bg={getClientTypeBadge(token.client_type)} className="ms-2">
                      {token.client_type}
                    </Badge>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleUnbindToken(token.id, selectedProxy.id)}
                  >
                    Unbind
                  </Button>
                </ListGroup.Item>
              ))}
              {getProxyTokens(selectedProxy.id).length === 0 && (
                <ListGroup.Item className="text-muted text-center">
                  No tokens bound to this proxy
                </ListGroup.Item>
              )}
            </ListGroup>
              )}
            </Col>
              <Col md={6}>
                <h5 className="fw-bold mb-3">Available Tokens</h5>
                <ListGroup variant="flush">
                  {availableTokens.map(token => (
                    <ListGroup.Item key={token.id} className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-medium">{token.label || 'Unlabeled'}</div>
                        <small className="text-muted font-monospace">{maskToken(token.token)}</small>
                        <Badge bg={getClientTypeBadge(token.client_type)} className="ms-2">
                          {token.client_type}
                        </Badge>
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleBindToken(token.id)}
                        disabled={token.proxy_id === selectedProxy.id}
                      >
                        {token.proxy_id === selectedProxy.id ? 'Bound' : 'Bind'}
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button variant="outline-secondary" onClick={() => setShowTokenModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold text-danger">
            <AlertTriangle size={24} className="me-2" />
            Delete Proxy
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="text-center mb-3">
            <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
              <Trash2 size={32} className="text-danger" />
            </div>
            <h5 className="fw-bold">Are you sure you want to delete this proxy?</h5>
            {proxyToDelete && (
              <div className="mt-3 p-3 bg-light rounded">
                <div className="fw-medium font-monospace">{proxyToDelete.proxy}</div>
                <div className="mt-2">
                  <Badge bg={getStatusVariant(proxyToDelete.is_active)} className="me-1">
                    {proxyToDelete.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge bg="info" className="me-1">
                    {getProxyTypeFromString(proxyToDelete.proxy)}
                  </Badge>
                </div>
                <div className="mt-2 small">
                  <div>Success: {proxyToDelete.success_count || 0}</div>
                  <div>Fails: {proxyToDelete.fail_count || 0}</div>
                  <div>Total: {proxyToDelete.usage_count || 0}</div>
                </div>
              </div>
            )}
            <div className="text-danger mt-3">
              <AlertTriangle size={16} className="me-1" />
              This action cannot be undone.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0 justify-content-center">
          <Button variant="outline-secondary" onClick={handleDeleteCancel} className="px-4">
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} className="px-4">
            Delete Proxy
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            <Upload size={20} className="me-2 text-primary" />
            Bulk Upload Proxies
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Tabs
            activeKey={uploadType}
            onSelect={(k) => setUploadType(k)}
            className="mb-3"
          >
            <Tab eventKey="csv" title="CSV Upload">
              <div className="mb-3">
                <p className="text-muted">
                  Upload a CSV file with your proxy data. The file should include the following columns:
                </p>
                <ul className="text-muted">
                  <li><code>proxy</code> - Proxy address (required)</li>
                  <li><code>is_active</code> - Active status (true/false, defaults to true)</li>
                </ul>
              </div>
            </Tab>
            <Tab eventKey="json" title="JSON Upload">
              <div className="mb-3">
                <p className="text-muted">
                  Upload a JSON file with your proxy data. The file should be an array of objects with the following properties:
                </p>
                <ul className="text-muted">
                  <li><code>proxy</code> - Proxy address (required)</li>
                  <li><code>is_active</code> - Active status (boolean, defaults to true)</li>
                </ul>
              </div>
            </Tab>
          </Tabs>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="fw-medium">Select File</Form.Label>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={downloadTemplate}
              >
                <Download size={14} className="me-1" />
                Download Template
              </Button>
            </div>
            <Form.Control
              type="file"
              accept={uploadType === 'csv' ? '.csv' : '.json'}
              onChange={handleFileSelect}
              ref={fileInputRef}
            />
          </div>

          {uploadResult && uploadResult.errors && uploadResult.errors.length > 0 && (
            <Alert variant="warning" className="mt-3">
              <AlertTriangle size={18} className="me-2" />
              <strong>Upload completed with {uploadResult.errors.length} errors:</strong>
              <ul className="mb-0 mt-2">
                {uploadResult.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button variant="outline-secondary" onClick={() => setShowUploadModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpload}
            disabled={!uploadFile || uploading}
          >
            {uploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} className="me-2" />
                Upload
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Proxies;